import axios from "axios";

function getErrorResponse(error: unknown) {
  if (!axios.isAxiosError(error)) return undefined;

  return (
    error.response ?? {
      status: 0,
      data: { error: error.message ?? "Network error" },
      headers: {},
    }
  );
}

export async function getAllUsersRequest() {
  return await axios.get("/multiuser");
}

export async function getAllUsers() {
  try {
    const { data } = await getAllUsersRequest();
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export interface SeatUsage {
  currentCount: number | null;
  maximumUsers: number | null;
  atLimit: boolean;
  isOverLimit: boolean;
  status: string;
  // SKE-5085: absent from a server that predates the field.
  inviteBlocked?: boolean;
}

// SKE-4016: authoritative seat usage from the server — the same count the
// invite/login gates enforce on (admin + active collaborators), so the users
// page can't drift from enforcement. Note the /api prefix: this route is
// `@Route("api/multiuser")`, unlike the un-prefixed /multiuser routes above.
export async function getSeatUsage(): Promise<SeatUsage | undefined> {
  try {
    const { data } = await axios.get("/api/multiuser/seat-usage");
    return data as SeatUsage;
  } catch {
    // Non-fatal: the users list falls back to a client-side estimate.
    return undefined;
  }
}

export async function addUser(user: any) {
  try {
    const { data } = await axios.post("/multiuser/", user);
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export async function deleteAvatar({ email }: { email: string; id?: string }) {
  try {
    const { data } = await axios.delete(`/multiuser/${email}/profile`);
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export async function updateAvatar({
  email,
  url,
}: {
  email: string;
  url: string;
  id?: string;
}) {
  try {
    const { data } = await axios.put(`/multiuser/${email}/profile`, { url });
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export async function getUser(email: string) {
  try {
    const { data } = await axios.get(`/multiuser/${email}`);
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export async function updateUser({
  email,
  ids,
}: {
  email: string;
  ids?: string[];
}) {
  try {
    const { data } = await axios.put(`/multiuser/${email}`, { ids });
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export async function updateUserPreferences({
  userId,
  preferences,
}: {
  userId: string;
  preferences: any;
}) {
  try {
    const { data } = await axios.put(`/multiuser/setPreferences/${userId}`, {
      ...preferences,
    });
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}

export async function deleteUser({ email }: { email: string }) {
  try {
    const { data } = await axios.delete(`/multiuser/${email}`);
    return data;
  } catch (error) {
    return getErrorResponse(error);
  }
}
